<template>
	<div class="item-editor">
		<div class="editor-row-half">
			<div class="editor-column">
				<AppTextInput ref="name-input" class="editor-input" :label="getString('name')" :placeholder="getString('product_name')" :value="item.name" @changed-text="(value) => item.name = value" />
			</div>
			<div class="editor-column">
				<div class="editor-row-half-half">
					<div class="editor-column">
						<AppNumberInput double-decimal :label="getString('price_symbol', [currencyStore.currCurrency.symbol])" placeholder="0" :min="0" :value="currencyStore.dbToActualValue(item.price)" @changed-value="changedItemValue" />
					</div>
					<div class="editor-column column-right">
						<div class="availability-container">
							<AppSwitch :label="getString('available')" :checked="!!item.available || item.available === null" @switched="(value) => item.available = value" />
							<AppButtonIcon :active="item.available === null" prevent-change class="ml-10" icon-name="icon-hourglass" small @click="openTimetables" />
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="editor-row-half mt-20">
			<div class="editor-column">
				<AppTextInput class="editor-input" textarea :label="getString('description')" :placeholder="getString('product_description')" :value="item.ingredients" @changed-text="(value) => item.ingredients = value" />
			</div>
			<div class="editor-column">
				<div class="editor-row-half-half" :class="{noAllergens: userStore.currBusiness && userStore.currBusiness.type === 'other'}">
					<div class="editor-column-40">
						<AppDropzone :label="getString('image')"
							create-thumbnail
							 :max-bytes="600 * 1024"
							 :max-width="1024"
							 :max-height="1024"
							:files="item.picture ? [item.picture] : []"
							@removed-file="(url, newFiles) => item.picture = newFiles && newFiles.length ? newFiles[0] : null"
							@upload-complete="(files) => item.picture = files[0]" />
					</div>
					<div class="editor-column-60 column-right" v-if="userStore.currBusiness && userStore.currBusiness.type !== 'other'">
						<div class="allergens-button-container">
							<p class="txt-input-label">{{ getString('allergens') }}</p>
							<AppButton :text="getAllergensButtonText()" @click="openAllergensSelector" outline />
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="buttons-container">
			<AppButton :class="{'active-button': item.composite}" small @click="openComposite" :text="getString('variations')" outline />
			<AppButton :class="{'active-button': item.deepen}" small @click="deepenSheetActive = true" :text="getString('detailed')" outline />
			<AppButton small @click="advancedSheetActive = true" :text="getString('advanced_plural')" outline />
		</div>
		<div class="save-container mb-10 mt-20">
			<div class="save-button-container">
				<AppButton :text="getString('save')" @click="saveItem" />
				<AppClickableIcon :autoSetActive="false" class="delete-button" icon-name="iconBin" @click="deleteItem" />
			</div>
		</div>
	</div>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="compositeSheetActive" @toggling-popup="toggleCompositeSheet">
		<div class="composite-content" v-if="compositeSheetActive">
			<div class="row centered">
				<AppLoader v-if="loadingComposite" />
			</div>
			<CompositeEditor :categoryTempId="categoryTempId" :itemTempId="itemTempId" @save="saveCompositeSheet" v-if="!loadingComposite && loadedComposite" />
		</div>
	</AppBottomSheet>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="deepenSheetActive" @toggling-popup="toggleDeepenSheet">
		<DeepenEditor v-if="deepenSheetActive" :categoryTempId="categoryTempId" :itemTempId="item.tempid" @save-clicked="saveDeepenSheet" />
	</AppBottomSheet>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="advancedSheetActive" @toggling-popup="toggleAdvancedSheet">
		<AdvancedOptionsEditor v-if="advancedSheetActive" :categoryTempId="categoryTempId" :itemTempId="item.tempid" @save-clicked="saveAdvancedSheet" />
	</AppBottomSheet>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="timetablesSheetActive" @toggling-popup="toggleTimetablesSheet">
		<div class="timetables-content" v-if="timetablesSheetActive">
			<div class="row centered">
				<AppLoader v-if="loadingTimetables" />
			</div>
			<TimetablesEditor v-if="!loadingTimetables && loadedTimetables" :menu-item-id="item.menu_item_id ? Number(item.menu_item_id) : 0" :title="getString('x_time_slots', [item.name])" with-quantity :timetables="timetables" @save-clicked="timetablesSave" />
		</div>
	</AppBottomSheet>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="allergensSheetActive" @toggling-popup="toggleAllergensSheet">
		<AllergensSelector v-if="allergensSheetActive" :selected-allergens="item.allergens_link" @confirm-selection="confirmAllergenSelection" />
	</AppBottomSheet>
</template>

<script>
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppDropzone from "@/shared/components/AppDropzone.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import CompositeEditor from "@/console-new/components/config/menu/CompositeEditor.vue";
import TimetablesEditor from "@/console-new/components/shared/TimetablesEditor.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AdvancedOptionsEditor from "@/console-new/components/config/menu/AdvancedOptionsEditor.vue";
import DeepenEditor from "@/console-new/components/config/menu/DeepenEditor.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AllergensSelector from "@/console-new/components/config/menu/AllergensSelector.vue";

import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, useUnchangingStore, useUserStore} from "@/console-new/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let userStore = useUserStore();
		let unchangingStore = useUnchangingStore();
		let notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, menuStore, currencyStore, userStore, unchangingStore};
	},
	emits: ["item-saved"],
	mixins: [WindowWidth],
	name: "ItemEditor",
	components: {AppLoader, AppButtonIcon, AppNumberInput, AppTextInput, AppSwitch, AppDropzone, AppButton, AppBottomSheet, CompositeEditor, TimetablesEditor, AdvancedOptionsEditor, DeepenEditor, AppClickableIcon, AllergensSelector},
	props: {
		itemTempId: {
			type: String,
			required: true
		},
		categoryTempId: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			compositeSheetActive: false,
			advancedSheetActive: false,
			deepenSheetActive: false,
			timetablesSheetActive: false,
			allergensSheetActive: false,
			loadingTimetables: false,
			loadedTimetables: false,
			timetables: [],
			loadingComposite: false,
			loadedComposite: false,
			composedLists: []
		};
	},
	computed: {
		item() {
			return this.menuStore.modItemFromTempIds(this.categoryTempId, this.itemTempId);
		},
		allergensList() {
			return this.unchangingStore.orderedAllergens.map((allergen) => this.getString(allergen.name_key));
		},
		selectedAllergensIndexes() {
			let indexes = [];
			let allergens = this.unchangingStore.orderedAllergens;
			this.item.allergens_link.forEach((allergenLink) => {
				let index = allergens.findIndex((allergen) => allergen.allergen_id === allergenLink.allergen_id);
				if(index !== -1) {
					indexes.push(index);
				}
			});
			return indexes;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		}
	},
	methods: {
		changedItemValue(value) {
			this.item.price = this.currencyStore.actualToDbValue(value)
		},
		toggleCompositeSheet(isOpen) {
			if(!isOpen) {
				this.compositeSheetActive = false;
				this.loadedComposite = false;
			}
		},
		toggleAdvancedSheet(isOpen) {
			if(!isOpen) {
				this.advancedSheetActive = false;
				if(this.item.full_price && this.item.full_price === this.item.price) {
					this.item.full_price = null;
				}
			}
		},
		toggleDeepenSheet(isOpen) {
			if(!isOpen) {
				this.deepenSheetActive = false;
			}
		},
		timetablesSave() {
			this.item.available = null;
			this.toggleTimetablesSheet(false);
			this.saveItem(false, false);
		},
		toggleTimetablesSheet(isOpen) {
			if(!isOpen) {
				this.timetablesSheetActive = false;
				this.loadedTimetables = false;
			}
		},
		saveAdvancedSheet() {
			this.toggleAdvancedSheet();
			this.saveItem(false);
		},
		saveDeepenSheet() {
			this.toggleDeepenSheet();
			this.saveItem(false);
		},
		saveCompositeSheet() {
			this.toggleCompositeSheet();
			this.saveItem(false);
		},
		openTimetables() {
			if(this.item.menu_item_id) {
				this.timetablesSheetActive = true;
				this.loadingTimetables = true;
				this.loadedTimetables = false;
				let self = this;
				this.menuStore.getItemTimetables(this.item.menu_item_id, function(timetables) {
					self.loadingTimetables = false;
					self.loadedTimetables = true;
					self.timetables = timetables;
				});
			} else {
				this.showNotification("timetable-save-item");
			}
		},
		openComposite() {
			if(this.item.menu_item_id) {
				this.loadedComposite = false;
				this.compositeSheetActive = true;
				this.loadingComposite = true;
				let self = this;
				this.menuStore.getCompositeProduct(this.item.menu_item_id, function() {
					self.loadingComposite = false;
					self.loadedComposite = true;
				});
			} else {
				this.showNotification("composed-save-item");
			}
		},
		getAllergensButtonText() {
			const count = this.item.allergens_link.length;
			if (count === 0) {
				return this.getString('choose_allergens');
			} else if (count === 1) {
				return this.getString('1_selected');
			} else {
				return this.getString('n_selected', [count]);
			}
		},
		openAllergensSelector() {
			this.allergensSheetActive = true;
		},
		toggleAllergensSheet(isOpen) {
			if (!isOpen) {
				this.allergensSheetActive = false;
			}
		},
		confirmAllergenSelection(selectedAllergens) {
			this.item.allergens_link = selectedAllergens;
			this.toggleAllergensSheet(false);
		},
		saveItem(closeDropdown = true, notify = true) {

			if(this.item.full_price && this.item.full_price > 0 && this.item.full_price < this.item.price) {
				this.showNotification("item-price-discount-invalid");
				return;
			}
			if(this.item.full_price && this.item.full_price === this.item.price) {
				this.item.full_price = null;
			}

			this.menuStore.saveSingleItem(this.item, (success, details) => {
				if(success) {
					if(details === "inserted") {
						notify && this.showNotification("item-inserted");
					} else if(details === "updated") {
						notify && this.showNotification("item-updated");
					} else {
						notify && this.showNotification("generic-success");
					}
					this.$emit("item-saved", this.item, closeDropdown);
				} else {
					if(details === "item_price_mode") {
						let minHidePricesValue = this.currencyStore.readableCurrencyValue(this.currencyStore.currCurrency.min_hide_prices_amount);
						this.showNotification("item-price-mode", [minHidePricesValue]);
					} else if(details === "slug_mismatch") {
						this.showNotification("item-slug-error");
					} else if(details === "auth_denied") {
						this.showNotification("auth-denied-error");
					} else {
						this.showNotification("generic-fail");
					}
				}
			});
		},
		deleteItem() {
			if(this.categoryTempId) {
				delete this.menuStore.modMenu.categories[this.categoryTempId].items[this.itemTempId];
			} else {
				delete this.menuStore.modMenu.orphanItems[this.itemTempId];
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.editor-row-half {
	display: flex;
	justify-content: space-between;
	width: 100%;


	.column-right {
		display: flex;
		justify-content: flex-end;
	}

	.editor-column {
		width: 50%;
	}

	.editor-column-40 {
		width: 40%;
	}

	.editor-column-60 {
		width: 60%;
	}

	.editor-row-half-half {
		display: flex;
		justify-content: space-between;

		&.noAllergens {
			justify-content: flex-end;

			.editor-column-40 {
				width: 100%;
				display: flex;
				justify-content: flex-end;
			}
		}

		@media(max-width: 350px) {
			flex-direction: column;

			.editor-column, .editor-column-40, .editor-column-60 {
				width: 100%;
				display: flex;
				justify-content: center;

				&:first-child {
					margin-bottom: 20px;
				}
			}
		}

	}

	.availability-container {
		display: flex;
		justify-content: center;
		align-items: flex-end;
	}

	.editor-input {
		padding-right: 15px;
		max-width: 280px;
		width: 100%;
	}

	@media(max-width: 1400px) {
		flex-direction: column;

		.editor-column {
			width: 70%;
			margin: 0 auto;
		}

		.editor-column:nth-child(2) {
			margin-top: 20px;
		}

		.editor-row-half-half .editor-column:nth-child(2) {
			margin-top: 0;
		}

		.editor-input {
			padding-right: 0;
			max-width: unset;
		}

	}

	@media(max-width: 1280px) {

		.editor-column {
			width: 100%;
		}
	}

}
.buttons-container {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 65%;
	margin: 20px auto 0 auto;
	gap: 8px;

	@media(max-width: 1400px) {
		width: 70%;
	}
	@media(max-width: 1280px) {
		width: 100%;

		> .base-button {
			margin-top: 15px;
		}
	}
	@media(max-width: 400px) {
		flex-direction: column;
		justify-content: center;
	}
	:deep(.base-button) {
		&.active-button {
			background-color: var(--color-active-button);
			.button-inner .btn-text p {
				color: var(--color-active-button-text);
			}
		}
	}
}

.save-container {
	display: flex;
	justify-content: flex-end;
	width: 100%;

	.save-button-container {
		display: flex;
		align-items: center;

		.base-button {
			margin-right: 8px;
		}

		.delete-button {
			width: 20px;
			height: 20px;
			display: flex;

			:deep(svg) {
				padding: 3px;
			}
		}
	}
}

.allergens-button-container {
	width: 100%;
	
	.txt-input-label {
		font-size: 12px;
		color: var(--color-txt-secondary);
		margin-bottom: 5px;
	}
	
	.base-button {
		width: 100%;
		max-width: 250px;
	}
}
</style>
